"use client";

import { useState } from "react";
import { StepProps, BaseFormData } from "../components/types";
import { cn } from "@/lib/utils";
import parse from "html-react-parser";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

// Configuration interface for selection options
export interface SelectionOption {
  id: string;
  title: string;
  description: string;
  redirectUrl: string;
  isSelected?: boolean;
}

// Configuration interface for StaticContentStep
export interface StaticContentStepConfig {
  title?: string;
  description?: string;
  selectionQuestion?: string;
  selectionOptions?: SelectionOption[];
  ctaButton?: {
    text: string;
    href?: string;
    onClick?: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
    size?: 'default' | 'sm' | 'lg';
    className?: string;
  };
  disclaimerText?: string;
  showProgressLine?: boolean;
  progressLineColor?: string;
}

type StaticContentStepProps<T = BaseFormData> = StepProps<T>;

export const StaticContentStep = <T extends BaseFormData = BaseFormData>(props: StaticContentStepProps<T>) => {
  const { 
    title, 
    description,
    onSubmit
  } = props;
  
  // Extract stepConfig with type safety
  const stepConfig = props.stepConfig as StaticContentStepConfig | undefined;

  const config = stepConfig || {
    title: "Information",
    description: "Please review the information below.",
    ctaButton: {
      text: "Continue",
      variant: "primary" as const,
      size: "lg" as const
    },
    disclaimerText: undefined,
    showProgressLine: false,
    progressLineColor: "#10b981" // Default green color
  };

  // State for selected option - no default selection
  const [selectedOption, setSelectedOption] = useState<string>("");

  const handleCtaClick = () => {
    if (config.selectionOptions && selectedOption) {
      // Find the selected option and redirect to its URL
      const selectedOptionData = config.selectionOptions.find(option => option.id === selectedOption);
      if (selectedOptionData) {
        window.location.href = selectedOptionData.redirectUrl;
        return;
      }
    }
    
    if (config.ctaButton?.onClick) {
      config.ctaButton.onClick();
    } else if (config.ctaButton?.href) {
      // If href is provided, navigate to the link
      window.location.href = config.ctaButton.href;
    } else {
      // Default behavior - proceed to next step
      onSubmit?.({} as T);
    }
  };

  return (
    <div className="flex flex-col gap-12">
      {/* Header */}
      <div className="flex flex-col gap-4">
        {title && (
          <h3 className="heading-lg text-foreground">{title}</h3>
        )}
        {description && (
          <p className="paragraph-lg text-foreground [&_a]:text-primary [&_a]:underline">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Selection Options */}
      {config.selectionOptions && config.selectionOptions.length > 0 && (
        <div className="flex flex-col gap-6">
          {config.selectionQuestion && (
            <h4 className="paragraph-base font-bold text-foreground">
              {config.selectionQuestion}
            </h4>
          )}
          
          <RadioGroup
            value={selectedOption}
            onValueChange={setSelectedOption}
            className="border border-border rounded-3xl gap-0"
          >
          {config.selectionOptions && config.selectionOptions.length > 0 && (
            config.selectionOptions?.map((option: SelectionOption, index: number) => (
              <Label 
                key={option.id}
                htmlFor={option.id}
                className={cn(
                  "w-full px-6 py-4 flex items-center gap-6 cursor-pointer hover:bg-muted transition-colors",
                  index === 0 && "rounded-t-3xl",
                  index === (config.selectionOptions?.length || 0) - 1 && "rounded-b-3xl",
                  index < (config.selectionOptions?.length || 0) - 1 && "border-b border-border"
                )}
              >
                <RadioGroupItem value={option.id} id={option.id} className="cursor-pointer" />
                <div className="flex-1">
                  <div className="flex flex-col gap-1 items-start justify-center">
                    <span className="paragraph-base text-foreground font-bold">{option.title}</span>
                    {option.description && (
                      <span className="paragraph-base text-muted-foreground font-normal">
                        {option.description}
                      </span>
                    )}
                  </div>
                </div>
                </Label>
              )))}
          </RadioGroup>
        </div>
      )}

      {/* CTA Button Section */}
      <div className="flex flex-col gap-4">
        {/* Progress line (optional) */}
        {config.showProgressLine && (
          <div className="flex items-center gap-4">
            <div 
              className="h-0.5 flex-1 border-t-2 border-dashed"
              style={{ borderColor: config.progressLineColor }}
            />
          </div>
        )}

        {/* CTA Button */}
        {config.ctaButton && (
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleCtaClick}
              variant={config.ctaButton.variant || "primary"}
              size={config.ctaButton.size || "lg"}
              className={cn("w-full", config.ctaButton.className)}
              disabled={config.selectionOptions && !selectedOption}
            >
              {config.ctaButton.text}
            </Button>
            
            {/* Disclaimer text */}
            {config.disclaimerText && (
              <p className="paragraph-sm text-muted-foreground text-center">
                {config.disclaimerText}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
